import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { redirectTo } from "@insite/client-framework/ServerSideRendering";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries, { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CreateAccountRssw from "@store/Components/Icons/CreateAccountRssw";
import { COLORS } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    newAccountText = "newAccountText", // Valtech Custom Code
    createAccountUseExternalLink = "createAccountUseExternalLink", // Valtech Custom Code
}

const mapStateToProps = (state: ApplicationState) => {
    const { search } = getLocation(state);
    const accountSettings = getSettingsCollection(state).accountSettings;
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    return {
        allowCreateAccount: accountSettings.allowCreateAccount,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.newAccountText]: string; // Valtech Custom Code
        [fields.createAccountUseExternalLink]: boolean; // Valtech Custom Code
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasHistory;

export interface StoreSignInCreateAccountRsswStyles {
    signInCreateNewAccountGridContainer?: GridContainerProps;
    signInCreateNewAccountTextGridItem?: InjectableCss; // Valtech Custom Code
    signInCreateNewAccountButtonGridItem?: InjectableCss; // Valtech Custom Code
    signInCreateNewAccountTitle?: TypographyProps;
    signInCreateNewAccountText?: TypographyProps;
    createNewAccountButton?: ButtonPresentationProps;
    // Valtech Custom Code ->
    containerGridItem?: GridItemProps;
    accountIconRssw?: IconProps;
    // <- Valtech Custom Code
}

export const storeSignInCreateAccountRsswStyles: StoreSignInCreateAccountRsswStyles = {
    // Valtech Custom Code ->
    signInCreateNewAccountGridContainer: {
        gap: 0,
        css: css`
            ${GridOffset} {
                ${({ theme }) =>
                    breakpointValuesMediaQueries(
                        theme,
                        [{ values: ["center", null, "flex-start", null, null], properties: ["justify-content"] }],
                        mediaOptions.min,
                    )}
            }
        `,
    },
    containerGridItem: {
        width: [10, 8, 12, 10, 10],
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    accountIconRssw: {
        src: CreateAccountRssw,
        size: 60,
        color: COLORS.secondary.base,
    },
    signInCreateNewAccountTitle: {
        variant: "h2",
        css: css`
            margin-top: 15px;
        `,
    },
    signInCreateNewAccountTextGridItem: {
        css: css`
            margin-top: 20px;
        `,
    },
    signInCreateNewAccountButtonGridItem: {
        css: css`
            margin-top: 45px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                        null,
                        null,
                        null,
                        css`
                            flex-direction: row;
                            justify-content: space-between;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    createNewAccountButton: { variant: "secondary" },
    // <- Valtech Custom Code
};

const styles = storeSignInCreateAccountRsswStyles;

const StoreSignInCreateAccountRssw: FC<Props> = ({ history, fields, allowCreateAccount, createAccountUrl }) => {
    if (!allowCreateAccount) {
        return null;
    }

    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }

        history.push(createAccountUrl);
    };

    return (
        <GridContainer {...styles.signInCreateNewAccountGridContainer}>
            {/* Valtech Custom Code -> */}
            <GridItem {...styles.containerGridItem}>
                <StyledWrapper>
                    <Icon {...styles.accountIconRssw} />
                    <Typography {...styles.signInCreateNewAccountTitle}>{translate("Create New Account")}</Typography>
                </StyledWrapper>
                {fields.newAccountText && (
                    <StyledWrapper {...styles.signInCreateNewAccountTextGridItem}>
                        <Typography {...styles.signInCreateNewAccountText}>{fields.newAccountText}</Typography>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.signInCreateNewAccountButtonGridItem}>
                    {createAccountUrl && !fields.createAccountUseExternalLink && (
                        <Button
                            {...styles.createNewAccountButton}
                            onClick={handleCreateNewAccount}
                            data-test-selector="signInCreateNewAccount_createNewAccount"
                        >
                            {translate("Create Account")}
                        </Button>
                    )}
                </StyledWrapper>
            </GridItem>
            {/* <- Valtech Custom Code */}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps)(StoreSignInCreateAccountRssw)),
    definition: {
        allowedContexts: ["SignInPage"],
        group: "Sign In",
        icon: "User",
        fieldDefinitions: [
            // Valtech Custom Code ->
            {
                name: fields.newAccountText,
                displayName: "New Account Text",
                editorTemplate: "MultilineTextField",
                defaultValue: "Create an account to checkout faster, view order history, save product lists, and more!",
                fieldType: "Translatable",
            },
            {
                name: fields.createAccountUseExternalLink,
                displayName: "Use external link ? (RSSW)",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            // <- Valtech Custom Code
        ],
    },
};

export default widgetModule;
